import { useState, useEffect, useCallback } from "react";
import Spinner from "../components/Spinner";
import { useAuth } from "../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_ORDER_SERVICE_URL;
export default function OrdersPage() {
  const { auth } = useAuth();
  const userId = auth?.userId;
  const [orders, setOrders] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(
    async (page = 0, size = 20) => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE}/orders/user/${userId}?page=${page}&size=${size}&sort=CreatedDate&direction=DESC`,
        );
        if (!res.ok) throw new Error("Failed to fetch orders");
        const data = await res.json();

        setOrders(data.content || []);
        setPageInfo({
          pageNumber: data.pageNumber,
          pageSize: data.pageSize,
          totalPages: data.totalPages,
        });
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    loadOrders(0, pageInfo.pageSize);
  }, [loadOrders, pageInfo.pageSize]);

  const renderStatus = (status) => {
    const map = {
      WAITING: "Waiting",
      PICKED: "Picked",
      SHIPPED: "Shipped",
      DELIVERED: "Delivered",
    };

    const color = {
      WAITING: "warning",
      PICKED: "info",
      SHIPPED: "primary",
      DELIVERED: "success",
    };

    return (
      <span
        className={`badge bg-${color[status]}-subtle text-${color[status]}`}
      >
        {map[status]}
      </span>
    );
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-4">
        <h4 className="mb-0 fw-semibold">Orders</h4>
      </div>

      {/* Orders Table */}
      <div className="table-responsive border rounded">
        {loading ? (
          <Spinner />
        ) : (
          <table className="table mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Order #</th>
                <th>Buyer ID</th>
                <th>Items</th>
                <th>Image</th>
                <th>Status</th>
                <th>Actions</th>
                <th>Message</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="fw-semibold">{order.id}</td>
                  <td>{order.buyerId}</td>
                  <td>{order.items?.length ?? order.items}</td>

                  <td>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        background: "#f1f1f1",
                        borderRadius: 4,
                      }}
                    />
                  </td>

                  <td>{renderStatus(order.status)}</td>

                  <td>
                    <div className="d-flex flex-column gap-1">
                      {order.status === "WAITING" && (
                        <>
                          <button className="btn btn-sm btn-outline-success">
                            Accept
                          </button>
                          <button className="btn btn-sm btn-outline-danger">
                            Cancel
                          </button>
                        </>
                      )}

                      {order.status !== "WAITING" && (
                        <button className="btn btn-sm btn-outline-secondary">
                          Print label
                        </button>
                      )}
                    </div>
                  </td>

                  <td>
                    <button className="btn btn-sm btn-outline-primary">
                      Message customer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <button
          className="btn btn-outline-primary"
          disabled={pageInfo.pageNumber === 0}
          onClick={() => loadOrders(pageInfo.pageNumber - 1, pageInfo.pageSize)}
        >
          ← Previous
        </button>
        <span>
          Page {pageInfo.pageNumber + 1} of {pageInfo.totalPages}
        </span>
        <button
          className="btn btn-outline-primary"
          disabled={pageInfo.pageNumber + 1 >= pageInfo.totalPages}
          onClick={() => loadOrders(pageInfo.pageNumber + 1, pageInfo.pageSize)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
